import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { MARKETING_URL, PLATFORM_API_URL, api } from "./api";
import type {
  AdminAffiliate,
  AffiliatePromotion,
  PromoImage,
  AffiliateRole,
  AffiliateTrendPoint,
  AdminProgram,
  Affiliate,
  AffiliateDetail,
  ClickSource,
  Commission,
  Link,
  LinkInsight,
  LinkPerformance,
  Overview,
  Payout,
  Program,
  ProgrammeStats,
  QueuedPayout,
  Referral,
  TrendPoint,
} from "./types";

/*
 * Server state is TanStack Query; UI state is `useState`. Nothing
 * server-derived is copied into component state — the rule CONVENTIONS.md
 * states for both other dashboards, and the one that keeps a stale copy of a
 * balance from outliving the balance.
 *
 * Every key is built here so an invalidation is greppable. A key spelled
 * inline in a component is a key that stops matching the day somebody adds a
 * parameter to it, and the symptom is a screen that does not refresh after a
 * write — which reads as the write having failed.
 */
export const keys = {
  overview: (days: number) => ["overview", days] as const,
  links: (archived: boolean) => ["links", archived] as const,
  referrals: () => ["referrals"] as const,
  commissions: () => ["commissions"] as const,
  payouts: () => ["payouts"] as const,
  me: () => ["me"] as const,
  promotions: () => ["promotions"] as const,
  media: () => ["media"] as const,
  /* Grouped under one prefix so a write invalidates the whole admin section at
     once — approving a payout moves the queue and the totals together. */
  adminStats: () => ["admin", "stats"] as const,
  adminAffiliates: (search: string) => ["admin", "affiliates", search] as const,
  adminQueue: () => ["admin", "queue"] as const,
  linkInsight: (id: string, days: number) => ["link", id, days] as const,
  sources: (days: number) => ["sources", days] as const,
  performance: (days: number) => ["performance", days] as const,
  trend: (days: number) => ["trend", days] as const,
  adminAffiliate: (id: string) => ["admin", "affiliate", id] as const,
  adminTrend: (days: number) => ["admin", "trend", days] as const,
  adminProgram: () => ["admin", "program"] as const,
  adminHistory: (status: string) => ["admin", "history", status] as const,
};

/**
 * Who is signed in, read from the cache `RequireSession` already filled.
 *
 * Every screen behind the session has this answer sitting in the query cache,
 * so asking for it again costs nothing and is not a second request. It exists
 * as a hook rather than as props threaded through the admin layout because the
 * thing that reads it is a button four levels down, and a prop chain for one
 * boolean is the shape that goes stale when somebody adds a route.
 *
 * **A hidden control is not a permission.** Every route the role gates is
 * refused by the API on the role in the session, which is the boundary; this
 * only stops the dashboard offering a button that would answer 403.
 */
export function useMe() {
  return useQuery({
    queryKey: keys.me(),
    queryFn: () => api<{ affiliate: Affiliate; program: Program }>("/v1/affiliate/me"),
  });
}

/**
 * The offers an affiliate may hand out.
 *
 * Read straight from the platform API rather than through this programme's own,
 * because that is where promotions live — see `PLATFORM_API_URL`. It is a
 * public, uncredentialed, minute-cached endpoint, so this is one plain `fetch`
 * with no envelope handling: there is no session to lose and no 401 to branch
 * on, and anything that goes wrong means "show no offers", which is also the
 * correct rendering for a deployment running none.
 *
 * Failures are swallowed into an empty list on purpose. A dashboard that
 * reports somebody else's service being unreachable, on a card that is an
 * extra rather than the point of the page, is a worse screen than one that
 * quietly has nothing to advertise.
 */
export function usePromotions() {
  return useQuery({
    queryKey: keys.promotions(),
    queryFn: async (): Promise<AffiliatePromotion[]> => {
      if (!PLATFORM_API_URL) return [];
      try {
        const response = await fetch(`${PLATFORM_API_URL}/v1/promotions/affiliate`, {
          credentials: "omit",
        });
        if (!response.ok) return [];
        const body = (await response.json()) as { data?: AffiliatePromotion[] };
        return body.data ?? [];
      } catch {
        return [];
      }
    },
    // Campaigns change when an operator changes them, which is rarely and never
    // while somebody watches. The endpoint caches for a minute at the edge; this
    // is the client's half of not asking more often than the answer moves.
    staleTime: 5 * 60_000,
  });
}

/**
 * The promotional images an affiliate can post.
 *
 * Read from the marketing site, which is where they are generated and served —
 * there is no API involved, and no state. Like the offers above, a failure
 * renders no card rather than an error: a media kit is an extra, and a
 * dashboard reporting a missing manifest helps nobody.
 */
export function useMedia() {
  return useQuery({
    queryKey: keys.media(),
    queryFn: async (): Promise<PromoImage[]> => {
      if (!MARKETING_URL) return [];
      try {
        const response = await fetch(`${MARKETING_URL}/promo/manifest.json`);
        if (!response.ok) return [];
        return (await response.json()) as PromoImage[];
      } catch {
        return [];
      }
    },
    // Static files behind a CDN. They change when somebody regenerates the
    // brand, which is not something to poll for.
    staleTime: 60 * 60_000,
  });
}

export function useOverview(days: number) {
  return useQuery({
    queryKey: keys.overview(days),
    queryFn: () => api<Overview>(`/v1/affiliate/overview?days=${days}`),
  });
}

export function useLinks(archived: boolean) {
  return useQuery({
    queryKey: keys.links(archived),
    queryFn: () =>
      api<{ data: Link[] }>(`/v1/affiliate/links${archived ? "?archived=1" : ""}`).then(
        (result) => result.data,
      ),
  });
}

/**
 * One page of a keyset-paginated list.
 *
 * `nextCursor` is null on the last page rather than a cursor that returns
 * nothing, so a client stops on the null and never on a short list — see the
 * overfetch note in the content API.
 */
interface Paged<T> {
  data: T[];
  nextCursor: string | null;
}

/**
 * The three lists, as infinite queries.
 *
 * `useInfiniteQuery` rather than the `Pagination` component the other two
 * dashboards share, and the reason is what that component needs: it renders
 * "1–25 of 340", and there is no 340 here. A total would be a second query over
 * the same predicate on every request, for a number nothing on these screens
 * shows.
 *
 * So the control is "Load more" and the rows accumulate. That also suits what
 * these lists are — an affiliate scans down their referrals looking for one, and
 * page numbers over an append-only history are a way to lose your place.
 *
 * `initialPageParam` is `undefined` because the first request carries no cursor
 * at all; the API defaults to the newest page.
 */
function pagedList<T>(path: string, key: readonly unknown[]) {
  return {
    queryKey: key,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      api<Paged<T>>(`${path}${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ""}`),
    getNextPageParam: (last: Paged<T>) => last.nextCursor ?? undefined,
  };
}

export function useReferrals() {
  return useInfiniteQuery(pagedList<Referral>("/v1/affiliate/referrals", keys.referrals()));
}

export function useCommissions() {
  return useInfiniteQuery(pagedList<Commission>("/v1/affiliate/commissions", keys.commissions()));
}

export function usePayouts() {
  return useInfiniteQuery(pagedList<Payout>("/v1/affiliate/payouts", keys.payouts()));
}

/** One campaign: the link, where its traffic came from, and its daily shape. */
export function useLinkInsight(linkId: string, days: number) {
  return useQuery({
    queryKey: keys.linkInsight(linkId, days),
    queryFn: () => api<LinkInsight>(`/v1/affiliate/links/${linkId}?days=${days}`),
  });
}

/** Every campaign's numbers inside the window, which is what ranks them. */
export function usePerformance(days: number) {
  return useQuery({
    queryKey: keys.performance(days),
    queryFn: () =>
      api<{ data: LinkPerformance[] }>(`/v1/affiliate/performance?days=${days}`).then(
        (r) => r.data,
      ),
  });
}

/** Where all of this affiliate's traffic came from. */
export function useSources(days: number) {
  return useQuery({
    queryKey: keys.sources(days),
    queryFn: () =>
      api<{ data: ClickSource[] }>(`/v1/affiliate/sources?days=${days}&limit=50`).then(
        (r) => r.data,
      ),
  });
}

export function useAffiliateTrend(days: number) {
  return useQuery({
    queryKey: keys.trend(days),
    queryFn: () =>
      api<{ data: AffiliateTrendPoint[] }>(`/v1/affiliate/trends?days=${days}`).then((r) => r.data),
  });
}

export function useCreateLink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string; label?: string; landingPath?: string }) =>
      api<{ link: Link }>("/v1/affiliate/links", { method: "POST", body: input }),
    // Both lists, because a link created while "show archived" is on belongs in
    // the list the reader is looking at. Two keys is cheaper than one key with a
    // filter applied client-side, which is the shape that goes stale.
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["links"] });
      void client.invalidateQueries({ queryKey: ["overview"] });
      void client.invalidateQueries({ queryKey: ["sources"] });
      void client.invalidateQueries({ queryKey: ["performance"] });
    },
  });
}

export function useUpdateLink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      label?: string;
      landingPath?: string;
      archived?: boolean;
    }) => {
      const { id, ...body } = input;
      return api<{ link: Link }>(`/v1/affiliate/links/${id}`, { method: "PATCH", body });
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: ["links"] }),
  });
}

export function useSetPayoutAddress() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (address: string) =>
      api<{ affiliate: Affiliate }>("/v1/affiliate/payout-address", {
        method: "PUT",
        body: { address },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["overview"] });
      void client.invalidateQueries({ queryKey: keys.me() });
    },
  });
}

export function useRequestPayout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ payout: Payout }>("/v1/affiliate/payouts", { method: "POST" }),
    // The balance moves with the payout — `payable` becomes `inPayout` — so the
    // overview is as stale as the payout list is after this.
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.payouts() });
      void client.invalidateQueries({ queryKey: ["overview"] });
      void client.invalidateQueries({ queryKey: keys.commissions() });
    },
  });
}

export function useCancelPayout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) =>
      api<{ payout: Payout }>(`/v1/affiliate/payouts/${payoutId}`, { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.payouts() });
      void client.invalidateQueries({ queryKey: ["overview"] });
      void client.invalidateQueries({ queryKey: keys.commissions() });
    },
  });
}

/* ------------------------------------------------------------------ admin
 *
 * Only reachable by an affiliate whose `role` is `admin`. The API enforces that
 * on every route; the nav hiding the section is a convenience, and a hidden
 * link has never been a security boundary — the rule CONVENTIONS.md states for
 * `useCan` in the other two dashboards.
 */

export function useProgrammeStats() {
  return useQuery({
    queryKey: keys.adminStats(),
    queryFn: () => api<{ stats: ProgrammeStats }>("/v1/affiliate/admin/stats").then((r) => r.stats),
  });
}

export function useAdminAffiliates(search: string) {
  return useQuery({
    queryKey: keys.adminAffiliates(search),
    queryFn: () =>
      api<{ data: AdminAffiliate[] }>(
        `/v1/affiliate/admin/affiliates${search ? `?search=${encodeURIComponent(search)}` : ""}`,
      ).then((r) => r.data),
    // The list is a search box; refetching it when the tab regains focus would
    // discard what somebody typed and is what the other dashboards turn off too.
    refetchOnWindowFocus: false,
  });
}

export function usePayoutQueue() {
  return useQuery({
    queryKey: keys.adminQueue(),
    queryFn: () =>
      api<{ data: QueuedPayout[] }>("/v1/affiliate/admin/payouts").then((r) => r.data),
  });
}

export function useSetAffiliateStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; status: string; reason?: string }) =>
      api(`/v1/affiliate/admin/affiliates/${input.id}/status`, {
        method: "PATCH",
        body: { status: input.status, reason: input.reason ?? null },
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["admin"] }),
  });
}

export function useSetAffiliateRole() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; role: AffiliateRole }) =>
      api(`/v1/affiliate/admin/affiliates/${input.id}/role`, {
        method: "PATCH",
        body: { role: input.role },
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["admin"] }),
  });
}

export function useApprovePayout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payoutId: string) =>
      api(`/v1/affiliate/admin/payouts/${payoutId}/approve`, { method: "POST" }),
    // The queue and the totals both move, and the affiliate's own payout list
    // does too if they happen to be looking at it.
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin"] });
      void client.invalidateQueries({ queryKey: keys.payouts() });
    },
  });
}

/**
 * Recording the transfer that paid a payout.
 *
 * The step that used to be a cron. Somebody sends USDC from the treasury and
 * pastes the hash; the API refuses anything that is not an approved payout, a
 * hash already recorded against another one, and a payout belonging to the
 * person recording it.
 */
export function useMarkPayoutPaid() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { payoutId: string; txHash: string }) =>
      api(`/v1/affiliate/admin/payouts/${input.payoutId}/paid`, {
        method: "POST",
        body: { txHash: input.txHash },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin"] });
      void client.invalidateQueries({ queryKey: keys.payouts() });
      // The affiliate's own balance moves with it — `inPayout` becomes `paid` —
      // so an admin who is also an affiliate sees their own figures update.
      void client.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

/** An approved payout that did not go out. Releases its commissions. */
export function useMarkPayoutFailed() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { payoutId: string; reason: string }) =>
      api(`/v1/affiliate/admin/payouts/${input.payoutId}/failed`, {
        method: "POST",
        body: { reason: input.reason },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin"] });
      void client.invalidateQueries({ queryKey: keys.payouts() });
      void client.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

export function useRejectPayout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { payoutId: string; reason: string }) =>
      api(`/v1/affiliate/admin/payouts/${input.payoutId}/reject`, {
        method: "POST",
        body: { reason: input.reason },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin"] });
      void client.invalidateQueries({ queryKey: keys.payouts() });
    },
  });
}

export function useAdminAffiliate(id: string) {
  return useQuery({
    queryKey: keys.adminAffiliate(id),
    queryFn: () => api<AffiliateDetail>(`/v1/affiliate/admin/affiliates/${id}`),
  });
}

export function useProgrammeTrend(days: number) {
  return useQuery({
    queryKey: keys.adminTrend(days),
    queryFn: () =>
      api<{ data: TrendPoint[] }>(`/v1/affiliate/admin/trends?days=${days}`).then((r) => r.data),
  });
}

export function useAdminProgram() {
  return useQuery({
    queryKey: keys.adminProgram(),
    queryFn: () =>
      api<{ program: AdminProgram }>("/v1/affiliate/admin/program").then((r) => r.program),
    // A form is initialised from this. Refetching it while somebody is halfway
    // through editing would not overwrite their input — the form holds its own
    // state — but it would move the "last changed" line under them mid-edit.
    refetchOnWindowFocus: false,
  });
}

/** The terms an operator used to change with `psql`. */
export function useUpdateProgramme() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      commissionPercent: number;
      commissionCap: number;
      minPayout: number;
      holdDays: number;
      cookieDays: number;
      acceptingReferrals: boolean;
      requiresApproval: boolean;
      termsUrl: string | null;
    }) =>
      api<{ program: AdminProgram }>("/v1/affiliate/admin/program", {
        method: "PATCH",
        body: input,
      }),
    /*
     * Every affiliate's own view of the terms changes with this, not just the
     * admin's — `/me` and `/overview` both carry the programme — so this
     * invalidates past the admin prefix. It is the one admin write that does.
     */
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin"] });
      void client.invalidateQueries({ queryKey: keys.me() });
      void client.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

/** The enforcement action a fraud review ends in. */
export function useVoidCommission() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { commissionId: string; reason: string }) =>
      api(`/v1/affiliate/admin/commissions/${input.commissionId}/void`, {
        method: "POST",
        body: { reason: input.reason },
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["admin"] }),
  });
}

export function usePayoutHistory(status: string) {
  return useInfiniteQuery({
    queryKey: keys.adminHistory(status),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
      const query = new URLSearchParams();
      if (status) query.set("status", status);
      if (pageParam) query.set("cursor", pageParam);
      const suffix = query.size > 0 ? `?${query.toString()}` : "";
      return api<{ data: QueuedPayout[]; nextCursor: string | null }>(
        `/v1/affiliate/admin/payouts/history${suffix}`,
      );
    },
    getNextPageParam: (last: { nextCursor: string | null }) => last.nextCursor ?? undefined,
  });
}
